import { useNavigate, useRoutes } from "react-router-dom";
import { Provider as ReduxProvider } from "react-redux";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { RTL } from "./components/rtl";
import { SplashScreen } from "./components/splash-screen";
import { SettingsDrawer } from "./components/settings/settings-drawer";
import { Toaster } from "./components/toaster";
import {
  SettingsConsumer,
  SettingsProvider,
} from "./contexts/settings-context";
// import { AuthConsumer, AuthProvider } from './contexts/auth/jwt-context';
import { AuthConsumer, AuthProvider, useAuth } from "./contexts/auth/firebase-context";

import { API_SERVICE_BACKEND, BASE_URL, gtmConfig, SERVER_DOMAIN, isLocalOrStagingEnv } from "./config";
import { useNprogress } from "./hooks/use-nprogress";
import { useAnalytics } from "./hooks/use-analytics";
import { routes } from "./routes";
import { store } from "./store";
import { createTheme } from "./theme";
// Remove if react-quill is not used
import "react-quill/dist/quill.snow.css";
// Remove if react-draft-wysiwyg is not used
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
// Remove if simplebar is not used
import "simplebar-react/dist/simplebar.min.css";
// Remove if mapbox is not used
import "mapbox-gl/dist/mapbox-gl.css";
// Remove if locales are not used
import "./locales/i18n";
import "react-datepicker/dist/react-datepicker.css";
import { useState, useEffect } from "react";
import { ReceiveEmailContext } from "./contexts/receiveEmail";
import {WhiteLabelProvider, WhiteLabelConsumer} from "./contexts/ClientConfiguration";
import { SidebarProvider } from "./contexts/sidebar-context";
import MetaTags from "./pages/components/MetaTags";
import GlobalStyles from "./globalStyles";
import axios from "axios";
import { getSessionStorage, setSessionStorage } from "./utils/storage";
import Cookies from "js-cookie";
import { useRouter } from "./hooks/use-router";
import { getAuth } from "firebase/auth";
import { firebaseApp } from "./libs/firebase";
import refreshAuthToken from "./utils/refreshAuthToken";


// Main apps

const queryClient = new QueryClient();

// Load feature flags only after auth + token (avoids getuserdetail 401 before /token API)
const FeatureFlagsLoader = () => {
  const { isAuthenticated } = useAuth();
  const email = getSessionStorage("userEmail");
  useEffect(() => {
    if (!isAuthenticated || !email) return;
    axios
      .get(`${API_SERVICE_BACKEND}/getuserdetail/${email}`)
      .then((res) => {
        sessionStorage.setItem(
          "featureFlagObject",
          JSON.stringify(res?.data?.data?.userSubscription?.subscriptions?.base?.features ?? {})
        );
      })
      .catch((err) => console.error("Error fetching user details:", err));
  }, [isAuthenticated, email]);
  return null;
};

// Load invite/workspace data only after auth + token (avoids list-inviteTo 401 before /token API)
const InviteToLoader = () => {
  const { isAuthenticated } = useAuth();
  const email = getSessionStorage("userEmail");
  useEffect(() => {
    if (!isAuthenticated || !email) return;
    axios
      .get(`${API_SERVICE_BACKEND}/list-inviteTo/${encodeURIComponent(email)}`)
      .then((res) => {
        setSessionStorage("userWdata", JSON.stringify(res?.data?.data ?? []));
      })
      .catch((err) => console.error("Error fetching list-inviteTo:", err));
  }, [isAuthenticated, email]);
  return null;
};

export const App = () => {
  useAnalytics(gtmConfig);
  useNprogress();

  useEffect(() => {
    if (window.opener && window.name === "OAuthPopup") {
      window.opener.postMessage("oauth_success", "*");
      window.close();
    }
  }, []);

  const element = useRoutes(routes);
  const navigate = useNavigate();

  const [receiveEmailData, setReceiveEmailData] = useState({});
  useEffect(() => {
    window.intercomSettings = {
      app_id: "qaecvjwi",
    };

    // Suppress ResizeObserver loop errors
    const resizeObserverErrorHandler = (e) => {
      if (
        e.message === 'ResizeObserver loop completed with undelivered notifications' ||
        e.message === 'ResizeObserver loop limit exceeded'
      ) {
        e.stopImmediatePropagation();
        e.stopPropagation();
      }
    };
    window.addEventListener('error', resizeObserverErrorHandler);
    return () => window.removeEventListener('error', resizeObserverErrorHandler);
  }, []);
  const email = sessionStorage.getItem("userEmail");
  useEffect(() => {
   
    if (email) {
      // featureFlagObject is already set at login; skip refetch on emailreplies
      if (window.location.pathname.includes("/dashboard/emailreplies")) return;

      axios.get(`${API_SERVICE_BACKEND}/getuserdetail/${email}`)
        .then(res => {
          //console.log(res?.data?.data?.userSubscription?.subscriptions?.base?.features, 'res =>>');
          sessionStorage.setItem('featureFlagObject', JSON.stringify(res?.data?.data?.userSubscription?.subscriptions?.base?.features));
        })
        .catch(error => {
           console.error('Error fetching user details:', error);
        });
    }

  }, [email]);
/*  const token = Cookies.get("b2b_at");
  useEffect(() => {
    const refreshToken = async () => {
      const auth = getAuth(firebaseApp);
      const currentUser = auth.currentUser;
      const firebaseToken = await currentUser?.getIdToken();
      if (!firebaseToken) {
        // Token expired: clear session and redirect to login
        Cookies.remove();
        sessionStorage.clear();
        localStorage.clear();
        setSessionStorage("currentUrl", "/auth-main/login/v2");
        navigate(`/auth-main/login/v2`); 
      }
      else {
        const response = refreshToken(firebaseToken)
      }
    }
    if(!token){
       refreshToken()
    }
  }, [token]) */

useEffect(() => {
  // Add Axios request interceptor to dynamically set Authorization header
  const axiosInterceptor = axios.interceptors.request.use(
    (config) => {
      const isGatewayDomain = config.url?.includes(SERVER_DOMAIN);
      if (isGatewayDomain) {
        config.withCredentials = true;
        if (isLocalOrStagingEnv()) {
          const localToken = window.sessionStorage.getItem('b2b_at_local');
          if (localToken) {
            config.headers.Authorization = `Bearer ${localToken}`;
          }
        }
      } else {
        delete config.headers.Authorization;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

 const axiosResponseInterceptor = axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;
      if (error.response && error.response.status === 401 && !originalRequest._retry) {
         originalRequest._retry = true;
         const auth = getAuth(firebaseApp);
         const currentUser = auth.currentUser;
         const firebaseToken = await currentUser.getIdToken();
        // if (!currentUser) {
        if (!firebaseToken) {
          // Token expired: clear session and redirect to login
          Cookies.remove("b2b_at");
          window.sessionStorage.removeItem('b2b_at_local');
          sessionStorage.clear();
          localStorage.clear();
          setSessionStorage("currentUrl", "/auth-main/login/v2");
          navigate(`/auth-main/login/v2`);
        }
        else {
          // Refresh token — backend sets new httpOnly cookie via Set-Cookie
          await refreshAuthToken(firebaseToken);
          return axios(originalRequest);
        }
      }
      return Promise.reject(error);
    }
  );
  // Store the original fetch function
  const originalFetch = window.fetch;
  // Override the global fetch function to dynamically add Authorization
  window.fetch = async (input, init = {}) => {
    const url = typeof input === 'string' ? input : input.url;
    const isGatewayDomain = url?.includes(SERVER_DOMAIN);
    if (isGatewayDomain) {
      init.credentials = 'include';
      if (isLocalOrStagingEnv()) {
        const localToken = window.sessionStorage.getItem('b2b_at_local');
        if (localToken) {
          init.headers = init.headers || {};
          if (init.headers instanceof Headers) {
            init.headers.set('Authorization', `Bearer ${localToken}`);
          } else {
            init.headers.Authorization = `Bearer ${localToken}`;
          }
        }
      }
    } else {
      // Remove Authorization header for non-gateway domains
      if (init.headers) {
        if (init.headers instanceof Headers) {
          init.headers.delete("Authorization");
        } else {
          delete init.headers.Authorization;
        }
      }
    }
    return originalFetch(input, init);
  };

  // Store the original fetch function
  const originalOpen = window.open;
  // Override the global fetch function to dynamically add Authorization
  window.open = async (url, name, features) => {
    let newUrl = url;
    const isGatewayDomain = url?.includes(SERVER_DOMAIN);
    if (isGatewayDomain) {
      try {
        // Caddy injects Authorization header from httpOnly cookie automatically
        const res = await axios.get(`${BASE_URL}/auth/auth/api/v1/token/tmp`, { headers: { "x-user-id": email } });
        newUrl = `${url}${url?.includes("?") ? "&" : "?"}_at=${res?.data?.token}`;
      }
      catch(err){
        console.log('err', err)
      }
    }
    return originalOpen(newUrl, name, features);
  };

  // Cleanup: Remove interceptor and restore fetch on unmount
  return () => {
    axios.interceptors.request.eject(axiosInterceptor);
    axios.interceptors.response.eject(axiosResponseInterceptor);
    window.fetch = originalFetch;
    window.open = originalOpen;
  };
}, []);


  return (
    <ReduxProvider store={store}>
      <QueryClientProvider client={queryClient}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <AuthProvider>
            <AuthConsumer>
              {(auth) => (
                <>
                  <FeatureFlagsLoader />
                  <InviteToLoader />
                <ReceiveEmailContext.Provider
                  value={{ receiveEmailData, setReceiveEmailData }}
                >
                  <WhiteLabelProvider>
                    <WhiteLabelConsumer>
                      {(config) => (
                        <SidebarProvider>
                          <SettingsProvider>
                            <SettingsConsumer>
                              {(settings) => {
                                // Prevent theme flicker when restoring custom settings from browser storage
                                if (!settings.isInitialized) {
                                  // return null;
                                }

                                const theme = createTheme({
                                  colorPreset: settings.colorPreset,
                                  contrast: settings.contrast,
                                  primaryTextColor: settings.primaryTextColor,
                                  secondaryTextColor: settings.secondaryTextColor,
                                  primaryBrandColor: settings.primaryBrandColor,
                                  secondaryBrandColor: settings.secondaryBrandColor,
                                  direction: settings.direction,
                                  paletteMode: settings.isNewInterfaceEnabled ? "dark" : "light", //settings.paletteMode,
                                  responsiveFontSizes: settings.responsiveFontSizes,
                                });

                                // Prevent guards from redirecting
                                const showSlashScreen = !auth.isInitialized;

                                return (
                                  <ThemeProvider theme={theme}>
                                    <MetaTags />
                                    <RTL direction={settings.direction}>
                                      <CssBaseline />
                                      <GlobalStyles mode={theme.palette.mode} isGlassmorphismOn={settings.isNewInterfaceEnabled} />

                                      {showSlashScreen &&
                                        !element?.props?.match?.route?.path?.includes(
                                          "chat/" ||
                                          element?.props?.match?.pathname.includes(
                                            "/chat/private"
                                          )
                                        ) ? (
                                        <SplashScreen />
                                      ) : (
                                        <>
                                          {element}
                                          {/* <SettingsButton onClick={settings.handleDrawerOpen} /> */}
                                          <SettingsDrawer
                                            canReset={settings.isCustom}
                                            onClose={settings.handleDrawerClose}
                                            onReset={settings.handleReset}
                                            onUpdate={settings.handleUpdate}
                                            open={settings.openDrawer}
                                            values={{
                                              colorPreset: settings.colorPreset,
                                              contrast: settings.contrast,
                                              direction: settings.direction,
                                              paletteMode: settings.paletteMode,
                                              responsiveFontSizes:
                                                settings.responsiveFontSizes,
                                              stretch: settings.stretch,
                                              layout: settings.layout,
                                              navColor: settings.navColor,
                                            }}
                                          />
                                        </>
                                      )}
                                      <Toaster />
                                    </RTL>
                                  </ThemeProvider>
                                );
                              }}
                            </SettingsConsumer>
                          </SettingsProvider>
                        </SidebarProvider>
                      )}
                    </WhiteLabelConsumer>
                  </WhiteLabelProvider>
                </ReceiveEmailContext.Provider>
                </>
              )}
            </AuthConsumer>
          </AuthProvider>
        </LocalizationProvider>
      </QueryClientProvider>
    </ReduxProvider>
  );
};
